import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Switch,
    TableContainer,
    TextField,
    Tooltip
} from "@mui/material";
import {
    Add,
    Cancel,
    CheckCircle,
    Edit,
    Link as LinkIcon,
    MoneyOffCsred,
    Receipt
} from "@mui/icons-material";
import {Widget} from "../components/Common";
import FlexStack from "../components/Common/flex-stack";
import {DataTable} from "../components/Table/data-table";
import {InfoBox} from "../components/Common/alerts";
import {LoadingFallback} from "../components/Common/loading-fallback";
import {useAdminProducts} from "../hooks/use-admin-products";
import {Product} from "../services/product-service";

type ProductFormState = {
    name: string;
    description: string;
    productType: string;
    currency: string;
    price: string;
    includesTax: boolean;
    isActive: boolean;
};

const emptyForm: ProductFormState = {
    name: '',
    description: '',
    productType: '',
    currency: 'EUR',
    price: '',
    includesTax: true,
    isActive: true,
};

const toFormState = (product: Product): ProductFormState => ({
    name: product.name,
    description: product.description,
    productType: product.productType,
    currency: product.currency,
    price: product.price === null ? '' : String(product.price),
    includesTax: product.includesTax,
    isActive: product.isActive,
});

const parsePrice = (price: string): number | null => {
    const trimmed = price.trim().replace(',', '.');
    return trimmed === '' ? null : Number(trimmed);
};

const isValidPrice = (price: string) => {
    const parsed = parsePrice(price);
    return parsed === null || (Number.isFinite(parsed) && parsed >= 0);
};

type ProductDialogProps = {
    open: boolean;
    product?: Product;
    pending: boolean;
    onClose: () => void;
    onSave: (form: ProductFormState) => void;
};

const ProductDialog = ({open, product, pending, onClose, onSave}: ProductDialogProps) => {
    const [form, setForm] = useState<ProductFormState>(emptyForm);

    useEffect(() => {
        if (open) {
            setForm(product ? toFormState(product) : emptyForm);
        }
    }, [open, product]);

    const update = <K extends keyof ProductFormState>(key: K, value: ProductFormState[K]) =>
        setForm(current => ({...current, [key]: value}));

    const valid = form.name.trim() !== ''
        && form.productType.trim() !== ''
        && form.currency.trim() !== ''
        && isValidPrice(form.price);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{product ? 'Produkt bearbeiten' : 'Produkt anlegen'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    <TextField label="Name" required value={form.name}
                               onChange={ev => update('name', ev.target.value)}/>
                    <TextField label="Beschreibung" multiline minRows={2} value={form.description}
                               onChange={ev => update('description', ev.target.value)}/>
                    <TextField label="Produkttyp" required value={form.productType}
                               disabled={!!product}
                               helperText={product ? 'Der Produkttyp kann nachträglich nicht geändert werden.' : undefined}
                               onChange={ev => update('productType', ev.target.value)}/>
                    <TextField label="Preis" value={form.price}
                               error={!isValidPrice(form.price)}
                               helperText="Leer lassen für ein Produkt ohne Preis."
                               onChange={ev => update('price', ev.target.value)}/>
                    <TextField label="Währung" required value={form.currency}
                               onChange={ev => update('currency', ev.target.value)}/>
                    <FormControlLabel label="Preis inkl. Steuer"
                                      control={<Switch checked={form.includesTax}
                                                       onChange={ev => update('includesTax', ev.target.checked)}/>}/>
                    {!!product && (
                        <FormControlLabel label="Aktiv"
                                          control={<Switch checked={form.isActive}
                                                           onChange={ev => update('isActive', ev.target.checked)}/>}/>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                <Button variant="contained" disabled={!valid || pending} onClick={() => onSave(form)}>
                    Speichern
                </Button>
            </DialogActions>
        </Dialog>
    );
};

type CreemLinkDialogProps = {
    product?: Product;
    pending: boolean;
    onClose: () => void;
    onSave: (creemProductId: string) => void;
};

const CreemLinkDialog = ({product, pending, onClose, onSave}: CreemLinkDialogProps) => {
    const [creemProductId, setCreemProductId] = useState('');

    useEffect(() => {
        setCreemProductId('');
    }, [product]);

    return (
        <Dialog open={!!product} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Creem-Produkt verknüpfen</DialogTitle>
            <DialogContent>
                <DialogContentText>Produkt: {product?.name}</DialogContentText>
                <TextField label="Creem-Produkt-ID" fullWidth required sx={{mt: 1}}
                           value={creemProductId}
                           onChange={ev => setCreemProductId(ev.target.value)}/>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Abbrechen</Button>
                <Button variant="contained" disabled={creemProductId.trim() === '' || pending}
                        onClick={() => onSave(creemProductId.trim())}>
                    Verknüpfen
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const formatPrice = (product: Product | undefined) => !product || product.price === null
    ? '–'
    : `${product.price.toLocaleString('de-DE', {minimumFractionDigits: 2})} ${product.currency}`;

const compareProducts = (a: Product, b: Product) =>
    Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name, 'de');

export const AdminProductsPage = () => {
    const {
        products = [],
        isPending,
        isError,
        createProduct,
        isCreateProductPending,
        updateProduct,
        isUpdateProductPending,
        linkCreemProduct,
        isLinkCreemProductPending,
    } = useAdminProducts();

    const productsById = useMemo(() => new Map(products.map(p => [p.productId, p])), [products]);
    const sortedProducts = useMemo(() => [...products].sort(compareProducts), [products]);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editedProduct, setEditedProduct] = useState<Product | undefined>(undefined);
    const [productToLink, setProductToLink] = useState<Product | undefined>(undefined);

    const openCreate = () => {
        setEditedProduct(undefined);
        setEditorOpen(true);
    };

    const openEdit = (product: Product) => {
        setEditedProduct(product);
        setEditorOpen(true);
    };

    const closeEditor = () => setEditorOpen(false);

    const handleSave = async (form: ProductFormState) => {
        const common = {
            name: form.name.trim(),
            description: form.description,
            currency: form.currency.trim(),
            price: parsePrice(form.price),
            includesTax: form.includesTax,
        };
        try {
            if (editedProduct) {
                await updateProduct({
                    productId: editedProduct.productId,
                    product: {...common, isActive: form.isActive},
                });
            } else {
                await createProduct({...common, productType: form.productType.trim()});
            }
            closeEditor();
        } catch {
            // Fehler wird bereits über den Snackbar angezeigt, Dialog bleibt zur Korrektur offen
        }
    };


    const handleLink = async (creemProductId: string) => {
        if (!productToLink) {
            return;
        }
        try {
            await linkCreemProduct({productId: productToLink.productId, creemProductId});
            setProductToLink(undefined);
        } catch {
            // Fehler wird bereits über den Snackbar angezeigt
        }
    };

    return (
        <FlexStack>
            <Widget displayName="Produkte">
                <Stack spacing={2}>
                    <div>
                        <Button variant="contained" startIcon={<Add/>} onClick={openCreate}>
                            Produkt anlegen
                        </Button>
                    </div>

                    {isPending && <LoadingFallback/>}
                    {isError && <InfoBox>Produkte konnten nicht geladen werden.</InfoBox>}
                    {!isPending && !isError && (products.length === 0
                        ? <InfoBox>Keine Produkte vorhanden.</InfoBox>
                        : <TableContainer>
                            <DataTable data={sortedProducts} columns={[
                                {id: 'name', header: 'Name', accessor: 'name'},
                                {id: 'productType', header: 'Typ', accessor: 'productType'},
                                {id: 'description', header: 'Beschreibung', accessor: 'description'},
                                {
                                    id: 'price',
                                    header: 'Preis',
                                    accessor: 'productId',
                                    sortBy: productId => productsById.get(String(productId))?.price ?? -1,
                                    render: productId => formatPrice(productsById.get(String(productId))),
                                },
                                {
                                    id: 'linkedProduct',
                                    header: 'Verknüpftes Produkt',
                                    accessor: 'linkedProduct',
                                    sortBy: linkedProduct => String(linkedProduct ?? ''),
                                    render: linkedProduct => linkedProduct ? String(linkedProduct) : '–',
                                },
                                {
                                    id: 'includesTax',
                                    header: 'Steuer',
                                    accessor: 'includesTax',
                                    sortBy: includesTax => Number(includesTax),
                                    render: includesTax => includesTax
                                        ? (
                                            <Tooltip title="Steuer inklusive">
                                                <Receipt color="success" aria-label="Steuer inklusive"/>
                                            </Tooltip>
                                        )
                                        : (
                                            <Tooltip title="Steuer nicht inklusive">
                                                <MoneyOffCsred color="disabled" aria-label="Steuer nicht inklusive"/>
                                            </Tooltip>
                                        ),
                                },
                                {
                                    id: 'isActive',
                                    header: 'Aktiv',
                                    accessor: 'isActive',
                                    sortBy: active => Number(active),
                                    render: active => active
                                        ? (
                                            <Tooltip title="Aktiv">
                                                <CheckCircle color="success" aria-label="Aktiv"/>
                                            </Tooltip>
                                        )
                                        : (
                                            <Tooltip title="Inaktiv">
                                                <Cancel color="disabled" aria-label="Inaktiv"/>
                                            </Tooltip>
                                        ),
                                },
                                {
                                    id: 'actions',
                                    accessor: 'productId',
                                    render: productId => {
                                        const p = productsById.get(String(productId));
                                        return !p ? null : (
                                        <Stack direction="row">
                                            <Tooltip title="Bearbeiten">
                                                <IconButton aria-label="Bearbeiten" onClick={() => openEdit(p)}>
                                                    <Edit/>
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Creem-Produkt verknüpfen">
                                                <IconButton aria-label="Creem-Produkt verknüpfen"
                                                            onClick={() => setProductToLink(p)}>
                                                    <LinkIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                        );
                                    },
                                },
                            ]}/>
                        </TableContainer>)}
                </Stack>
            </Widget>

            <ProductDialog open={editorOpen}
                           product={editedProduct}
                           pending={isCreateProductPending || isUpdateProductPending}
                           onClose={closeEditor}
                           onSave={form => void handleSave(form)}/>
            <CreemLinkDialog product={productToLink}
                             pending={isLinkCreemProductPending}
                             onClose={() => setProductToLink(undefined)}
                             onSave={id => void handleLink(id)}/>
        </FlexStack>
    );
};

export default AdminProductsPage;
